import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authApi, tasksApi, habitsApi } from '../api';
import { offlineStorage, habitOfflineStorage } from '../utils/offlineStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,     setUser]     = useState(null);
  const [loading,  setLoading]  = useState(true);  // true while checking auth on mount
  const [syncing,  setSyncing]  = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const syncedRef = useRef(false); // prevent double-sync in StrictMode

  // ── Boot: read token from localStorage, verify with /auth/me ─────────────
  useEffect(() => {
    const token = localStorage.getItem('dayrit_token');
    if (!token) {
      setLoading(false);
      return;
    }
    authApi.me()
      .then(res => {
        const u = res.data?.user ?? res.data;
        setUser(u);
      })
      .catch(() => {
        // Token invalid/expired — clear it
        localStorage.removeItem('dayrit_token');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Sync offline data whenever user logs in ───────────────────────────────
  useEffect(() => {
    if (!user) return;
    if (syncedRef.current) return;

    const taskCount  = offlineStorage.count();
    const habitCount = habitOfflineStorage.count();
    if (taskCount === 0 && habitCount === 0) return;

    syncedRef.current = true;
    syncAll();
  }, [user]);

  const syncAll = useCallback(async () => {
    setSyncing(true);
    setSyncDone(false);
    try {
      await syncOfflineTasks();
      await syncOfflineHabits();
      setSyncDone(true);
      setTimeout(() => setSyncDone(false), 4000);
    } catch (e) {
      console.error('Sync failed:', e);
    } finally {
      setSyncing(false);
    }
  }, []);

  // ── Sync tasks ────────────────────────────────────────────────────────────
  const syncOfflineTasks = async () => {
    const tasks = offlineStorage.getAll();
    if (!tasks.length) return;

    const failed = [];

    for (const task of tasks) {
      try {
        // Build payload — strip offline-only fields
        const payload = {
          title             : task.title,
          notes             : task.notes             || null,
          priority          : task.priority          || 'medium',
          due               : task.due               || 'today',
          due_date          : task.due_date          || null,
          done              : task.done              || false,
          sort_order        : task.sort_order        || 0,
          category_id       : task.category_id       || null,
          recurrence_type   : task.recurrence_type   || null,
          recurrence_config : typeof task.recurrence_config === 'object'
            ? JSON.stringify(task.recurrence_config)
            : task.recurrence_config || null,
        };

        const res     = await tasksApi.create(payload);
        const created = res.data?.data ?? res.data;

        // Sync subtasks under the new server task ID
        if (created?.id && Array.isArray(task.subtasks) && task.subtasks.length > 0) {
          for (const sub of task.subtasks) {
            try {
              await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/tasks/${created.id}/subtasks`, {
                method : 'POST',
                headers: {
                  'Content-Type' : 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('dayrit_token')}`,
                },
                body: JSON.stringify({ title: sub.title, done: sub.done || false }),
              });
            } catch { /* subtask sync failure is non-fatal */ }
          }
        }
      } catch {
        failed.push(task);
      }
    }

    // Replace storage with only the tasks that failed to sync
    if (failed.length === 0) {
      offlineStorage.clear();
    } else {
      try { localStorage.setItem('dayrit_offline_tasks', JSON.stringify(failed)); }
      catch { offlineStorage.clear(); }
    }
  };

  // ── Sync habits ───────────────────────────────────────────────────────────
  const syncOfflineHabits = async () => {
    const habits = habitOfflineStorage.getAll();
    if (!habits.length) return;

    const failed = [];

    for (const habit of habits) {
      try {
        const res     = await habitsApi.create({
          name       : habit.name,
          emoji      : habit.emoji       || '⭐',
          color      : habit.color       || 'var(--accent)',
          frequency  : habit.frequency   || 'daily',
          target_days: habit.target_days || [0,1,2,3,4,5,6],
        });
        const created = res.data?.data ?? res.data;

        // Replay completions
        if (created?.id && Array.isArray(habit.completions)) {
          for (const date of habit.completions) {
            try { await habitsApi.toggle(created.id, date); }
            catch { /* non-fatal */ }
          }
        }
      } catch {
        // Strip computed fields before saving back — keep only raw data
        const { week, streak, ...raw } = habit;
        failed.push(raw);
      }
    }

    if (failed.length === 0) {
      localStorage.removeItem('dayrit_offline_habits');
    } else {
      try { localStorage.setItem('dayrit_offline_habits', JSON.stringify(failed)); }
      catch { localStorage.removeItem('dayrit_offline_habits'); }
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    localStorage.removeItem('dayrit_token');
    syncedRef.current = false;
    setUser(null);
    setSyncDone(false);
  }, []);

  // ── login / register helpers (kept for backward compat if anything calls them) ──
  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    const { token, user: u } = res.data;
    localStorage.setItem('dayrit_token', token);
    setUser(u);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authApi.register(data);
    const { token, user: u } = res.data;
    if (token) localStorage.setItem('dayrit_token', token);
    if (u) setUser(u);
    return res.data;
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, syncing, syncDone,
      login, logout, register,
      refetchUser: () => {
        const token = localStorage.getItem('dayrit_token');
        if (!token) return;
        authApi.me().then(res => setUser(res.data?.user ?? res.data)).catch(() => {});
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}